import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  Inventory as ProductsIcon,
  ViewInAr as ModelsIcon,
  AccountTree as ProjectsIcon,
  Launch as LaunchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import ProductList from './StorefrontAdmin/ProductList';
import ProductEditor from './StorefrontAdmin/ProductEditor';
import ModelManager from './StorefrontAdmin/ModelManager';
import ProjectEditor from './StorefrontAdmin/ProjectEditor';
import CollectionCampaignSettings from './StorefrontAdmin/CollectionCampaignSettings';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const BrandStorefrontTab = ({ brandId, brandSlug, brandName, campaigns = [] }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [products, setProducts] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  // Load collections, projects, and available models on mount
  useEffect(() => {
    fetchCollections();
    fetchAvailableModels();
    fetchProjects();
  }, []);

  // Load products when collection changes
  useEffect(() => {
    if (selectedCollectionId) {
      loadCollectionProducts(selectedCollectionId);
    }
  }, [selectedCollectionId]);

  const fetchCollections = async () => {
    try {
      const response = await fetch('/api/collections/list', {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCollections(data.collections);
        // Auto-select first collection if available
        if (data.collections.length > 0 && !selectedCollectionId) {
          setSelectedCollectionId(data.collections[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch collections:', err);
      toast.error('Failed to load collections');
    }
  };

  const loadCollectionProducts = async (collectionId) => {
    setLoadingCollection(true);
    try {
      const response = await fetch(`/api/collections/${collectionId}/products`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
        setSelectedProductId(null);
      }
    } catch (err) {
      console.error('Failed to load collection products:', err);
      toast.error('Failed to load products');
    } finally {
      setLoadingCollection(false);
    }
  };

  const fetchAvailableModels = async () => {
    try {
      const response = await fetch('/api/models/list', {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const models = await response.json();
        setAvailableModels(models);
      }
    } catch (err) {
      console.error('Failed to fetch models:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handleSaveProduct = (productId, updatedData) => {
    setProducts((prev) => ({
      ...prev,
      [productId]: updatedData,
    }));
    toast.success('Product updated');
  };

  const handleSaveProject = (projectId, updatedData) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === projectId ? updatedData : p))
    );
    toast.success('Project updated');
  };

  const handleDeleteProject = async (projectId) => {
    const updatedProjects = projects.filter((p) => p.id !== projectId);
    setProjects(updatedProjects);
    setSelectedProjectId(null);

    try {
      await fetch('/api/projects', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projects: updatedProjects }),
      });
      toast.success('Project deleted');
    } catch (err) {
      console.error('Failed to delete project:', err);
      toast.error('Failed to delete project');
    }
  };

  const handleSaveAllProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projects }),
      });

      if (response.ok) {
        toast.success('All projects saved successfully');
      } else {
        toast.error('Failed to save projects');
      }
    } catch (err) {
      console.error('Failed to save projects:', err);
      toast.error('Error saving projects');
    }
  };

  const handleUpdateCollectionMetadata = async (updatedMetadata) => {
    if (!selectedCollectionId) return;

    setCollections((prev) =>
      prev.map((c) =>
        c.id === selectedCollectionId
          ? { ...c, metadata: updatedMetadata }
          : c
      )
    );

    try {
      await fetch('/api/collections/update-metadata', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          collectionId: selectedCollectionId,
          metadata: updatedMetadata
        }),
      });
      toast.success('Collection settings updated');
    } catch (err) {
      console.error('Failed to save collection metadata:', err);
      toast.error('Failed to save settings');
    }
  };

  const handleViewStorefront = () => {
    const url = `http://localhost:3002/${brandSlug}`;
    window.open(url, '_blank');
  };

  const handleEditStorefront = () => {
    if (!brandSlug) {
      toast.error('Brand slug is not available');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('You need to be logged in to edit the storefront');
      return;
    }

    const url = `http://localhost:3002/${brandSlug}?edit=true&token=${token}`;
    window.open(url, '_blank');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const selectedProduct = selectedProductId ? products[selectedProductId] : null;
  const selectedCollection = collections.find(c => c.id === selectedCollectionId);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Storefront Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage products, models, projects, and campaigns for {brandName}'s storefront
          </Typography>
        </Box>

        {/* Collection Selector */}
        {collections.length > 0 && (
          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel>Current Collection</InputLabel>
            <Select
              value={selectedCollectionId || ''}
              onChange={(e) => setSelectedCollectionId(e.target.value)}
              label="Current Collection"
              size="small"
            >
              {collections.map(collection => (
                <MenuItem key={collection.id} value={collection.id}>
                  {collection.name}
                </MenuItem>
              ))}
            </Select>
            {selectedCollection && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedCollection.description?.split('\n')[0]}
              </Typography>
            )}
          </FormControl>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<LaunchIcon />}
          onClick={handleViewStorefront}
        >
          View Storefront
        </Button>
        <Button
          variant="contained"
          startIcon={<LaunchIcon />}
          onClick={handleEditStorefront}
        >
          Edit Storefront
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Edit-in-Place:</strong> Click "Edit Storefront" to configure stage image, hotspots, and branding.
        Use the tabs below to manage your content library.
      </Alert>

      {/* Sub-tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab
            icon={<ProductsIcon />}
            iconPosition="start"
            label="Products"
          />
          <Tab
            icon={<ModelsIcon />}
            iconPosition="start"
            label="3D Models"
          />
          <Tab
            icon={<ProjectsIcon />}
            iconPosition="start"
            label="Projects"
          />
        </Tabs>
      </Box>

      {/* Products Tab */}
      <TabPanel value={currentTab} index={0}>
        {loadingCollection ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                Loading collection products...
              </Typography>
            </CardContent>
          </Card>
        ) : !selectedCollectionId ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" gutterBottom>
                No collections available
              </Typography>
              <Button
                variant="contained"
                href="/admin/collections"
                sx={{ mt: 2 }}
              >
                Import a Collection
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box>
            {/* Campaign Settings for Collection */}
            {selectedCollection && (
              <CollectionCampaignSettings
                collection={selectedCollection}
                projects={projects}
                campaigns={campaigns}
                onUpdate={handleUpdateCollectionMetadata}
              />
            )}

            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Product List */}
              <ProductList
                products={products}
                selectedId={selectedProductId}
                onSelect={setSelectedProductId}
              />

              {/* Product Editor */}
              {selectedProduct && selectedProductId ? (
                <ProductEditor
                  brandId={brandId}
                  brandSlug={brandSlug}
                  productId={selectedProductId}
                  product={selectedProduct}
                  availableModels={availableModels}
                  onSave={handleSaveProduct}
                />
              ) : (
                <Card sx={{ flex: 1 }}>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                      Select a product to edit
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* 3D Models Tab */}
      <TabPanel value={currentTab} index={1}>
        <ModelManager
          availableModels={availableModels}
          onRefresh={fetchAvailableModels}
          brandId={brandId}
          brandSlug={brandSlug}
        />
      </TabPanel>

      {/* Projects Tab */}
      <TabPanel value={currentTab} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Projects Management
              </Typography>
              <Button
                variant="contained"
                onClick={handleSaveAllProjects}
              >
                Save All Projects
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Projects List */}
              <Card variant="outlined" sx={{ minWidth: 300, maxWidth: 300 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Projects
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newId = `project-${Date.now()}`;
                        const newProject = {
                          id: newId,
                          name: 'New Project',
                          slug: newId,
                          type: 'Collaborative Project',
                          mission: '',
                          coreFocus: [],
                          impactStats: [],
                          videos: [],
                          ctaText: 'Learn More',
                          ctaLink: '/',
                          published: false
                        };
                        setProjects([...projects, newProject]);
                        setSelectedProjectId(newId);
                      }}
                    >
                      New
                    </Button>
                  </Box>

                  {projects.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      No projects yet
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {projects.map(project => (
                        <Card
                          key={project.id}
                          onClick={() => setSelectedProjectId(project.id)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: selectedProjectId === project.id ? 'primary.main' : 'background.paper',
                            color: selectedProjectId === project.id ? 'primary.contrastText' : 'text.primary',
                            '&:hover': {
                              bgcolor: selectedProjectId === project.id ? 'primary.dark' : 'action.hover',
                            }
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {project.name}
                              {!project.published && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                                  (Draft)
                                </Typography>
                              )}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Project Editor */}
              {selectedProjectId ? (
                <ProjectEditor
                  brandId={brandId}
                  brandSlug={brandSlug}
                  projectId={selectedProjectId}
                  project={projects.find((p) => p.id === selectedProjectId)}
                  onSave={handleSaveProject}
                  onDelete={handleDeleteProject}
                />
              ) : (
                <Card sx={{ flex: 1 }}>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                      Select a project to edit
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default BrandStorefrontTab;
